import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, User, Phone, Lock, Car, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const DriverRegister: React.FC = () => {
  const navigate = useNavigate();
  const { registerDriver } = useAuth();
  
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Personal Info
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Vehicle Info
  const [vehicleType, setVehicleType] = useState('economy');
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleYear, setVehicleYear] = useState('');
  const [vehicleColor, setVehicleColor] = useState('');
  const [plateNumber, setPlateNumber] = useState('');
  const [plateCity, setPlateCity] = useState('خرطوم');

  const vehicleTypes = [
    { id: 'economy', name: 'اقتصادي', desc: 'سيارة صغيرة، 4 ركاب' },
    { id: 'comfort', name: 'مريح', desc: 'سيارة متوسطة، 4 ركاب' },
    { id: 'family', name: 'عائلي', desc: 'سيارة عائلية، 6-7 ركاب' },
    { id: 'premium', name: 'VIP', desc: 'سيارة فاخرة، 4 ركاب' },
  ];

  const cities = ['خرطوم', 'أم درمان', 'بحري', 'مدني', 'بورتسودان', 'كسلا'];

  const validateStep1 = () => {
    if (!name.trim()) { setError('الرجاء إدخال الاسم'); return false; }
    if (!phone.trim() || phone.length < 10) { setError('الرجاء إدخال رقم هاتف صحيح'); return false; }
    if (password.length < 6) { setError('كلمة المرور يجب أن تكون 6 أحرف على الأقل'); return false; }
    if (password !== confirmPassword) { setError('كلمتا المرور غير متطابقتين'); return false; }
    return true;
  };

  const validateStep2 = () => {
    if (!vehicleMake.trim()) { setError('الرجاء إدخال ماركة السيارة'); return false; }
    if (!vehicleModel.trim()) { setError('الرجاء إدخال موديل السيارة'); return false; }
    if (!vehicleYear.trim()) { setError('الرجاء إدخال سنة الصنع'); return false; }
    if (!vehicleColor.trim()) { setError('الرجاء إدخال لون السيارة'); return false; }
    if (!plateNumber.trim()) { setError('الرجاء إدخال رقم اللوحة'); return false; }
    return true;
  };

  const handleNext = () => {
    setError('');
    if (step === 1 && validateStep1()) setStep(2);
  };

  const handleBack = () => {
    setError('');
    if (step === 1) navigate('/');
    else setStep(step - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validateStep2()) return;

    setIsLoading(true);
    try {
      await registerDriver({
        name,
        phone,
        password,
        vehicle: {
          type: vehicleType as any,
          make: vehicleMake,
          model: vehicleModel,
          year: parseInt(vehicleYear),
          color: vehicleColor,
          plateNumber,
          plateCity,
        },
      });
      navigate('/driver/home');
    } catch (err) {
      setError('حدث خطأ أثناء التسجيل');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-500 to-secondary-600 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="mb-6 text-white/80 hover:text-white flex items-center gap-2 transition-colors"
        >
          <ArrowRight className="w-5 h-5" />
          <span>{step === 1 ? 'رجوع' : 'السابق'}</span>
        </button>

        {/* Register Card */}
        <div className="bg-white rounded-3xl p-8 shadow-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-secondary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Car className="w-10 h-10 text-secondary-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">تسجيل كسائق</h1>
            <p className="text-gray-500">أكمل بياناتك للانضمام لفريق رحلة</p>
          </div>

          {/* Progress */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {[1, 2].map((s) => (
              <React.Fragment key={s}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  step >= s ? 'bg-secondary-500 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {s}
                </div>
                {s < 2 && <div className={`w-16 h-1 rounded ${step >= s + 1 ? 'bg-secondary-500' : 'bg-gray-200'}`} />}
              </React.Fragment>
            ))}
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-red-600 text-sm text-center">{error}</p>
            </div>
          )}

          {/* Step 1: Personal Info */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">الاسم الكامل</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="أدخل اسمك الكامل"
                  className="w-full border border-gray-200 rounded-xl py-3 px-4 focus:outline-none focus:border-secondary-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">رقم الهاتف</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="مثال: 0912345678"
                  className="w-full border border-gray-200 rounded-xl py-3 px-4 focus:outline-none focus:border-secondary-500"
                  dir="ltr"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">كلمة المرور</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="6 أحرف على الأقل"
                  className="w-full border border-gray-200 rounded-xl py-3 px-4 focus:outline-none focus:border-secondary-500"
                  dir="ltr"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">تأكيد كلمة المرور</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="أعد إدخال كلمة المرور"
                  className="w-full border border-gray-200 rounded-xl py-3 px-4 focus:outline-none focus:border-secondary-500"
                  dir="ltr"
                />
              </div>
              <button
                onClick={handleNext}
                className="w-full bg-secondary-500 text-white font-semibold py-4 rounded-xl hover:bg-secondary-600 transition-colors"
              >
                التالي
              </button>
            </div>
          )}

          {/* Step 2: Vehicle Info */}
          {step === 2 && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Vehicle Type */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">نوع السيارة</label>
                <div className="grid grid-cols-2 gap-2">
                  {vehicleTypes.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setVehicleType(type.id)}
                      className={`p-3 rounded-xl border-2 text-sm transition-colors ${
                        vehicleType === type.id
                          ? 'border-secondary-500 bg-secondary-50'
                          : 'border-gray-200'
                      }`}
                    >
                      <p className="font-medium">{type.name}</p>
                      <p className="text-xs text-gray-500">{type.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">الماركة</label>
                  <input
                    type="text"
                    value={vehicleMake}
                    onChange={(e) => setVehicleMake(e.target.value)}
                    placeholder="مثال: تويوتا"
                    className="w-full border border-gray-200 rounded-xl py-3 px-4 focus:outline-none focus:border-secondary-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">الموديل</label>
                  <input
                    type="text"
                    value={vehicleModel}
                    onChange={(e) => setVehicleModel(e.target.value)}
                    placeholder="مثال: كورولا"
                    className="w-full border border-gray-200 rounded-xl py-3 px-4 focus:outline-none focus:border-secondary-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">سنة الصنع</label>
                  <input
                    type="number"
                    value={vehicleYear}
                    onChange={(e) => setVehicleYear(e.target.value)}
                    placeholder="2020"
                    className="w-full border border-gray-200 rounded-xl py-3 px-4 focus:outline-none focus:border-secondary-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">اللون</label>
                  <input
                    type="text"
                    value={vehicleColor}
                    onChange={(e) => setVehicleColor(e.target.value)}
                    placeholder="أبيض"
                    className="w-full border border-gray-200 rounded-xl py-3 px-4 focus:outline-none focus:border-secondary-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">رقم اللوحة</label>
                  <input
                    type="text"
                    value={plateNumber}
                    onChange={(e) => setPlateNumber(e.target.value)}
                    placeholder="12345"
                    className="w-full border border-gray-200 rounded-xl py-3 px-4 focus:outline-none focus:border-secondary-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">المدينة</label>
                  <select
                    value={plateCity}
                    onChange={(e) => setPlateCity(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl py-3 px-4 focus:outline-none focus:border-secondary-500"
                  >
                    {cities.map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-secondary-500 text-white font-semibold py-4 rounded-xl hover:bg-secondary-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>إنشاء الحساب</span>
                  </>
                )}
              </button>
            </form>
          )}

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-500">
              لديك حساب بالفعل؟{' '}
              <button 
                onClick={() => navigate('/driver/login')}
                className="text-secondary-600 font-medium hover:underline"
              >
                سجل دخول
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverRegister;
